import { Map } from "mapbox-gl";
import { useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { getRobotStatusBackgroundColor, getRobotStatusMainColor } from "../../../utils/ColourStatus";
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';

export const DEFAULT_MODEL_ROTATION_ADJUSTMENT = 180; // Adjust rotation to face north
const DEFAULT_MODEL_SCALE = 1; // Default scale for the model
// const DEFAULT_MODEL_FILE = '/new_robot.glb'; // Default model file path
const DEFAULT_MODEL_FILE = '/new_robot.compressed.glb'; // Default model file path
export default function useLoadRobotModelIcon({
  mapRef
}: {
  mapRef: Map | null;
}) {
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const sceneRef = useRef<THREE.Scene>(new THREE.Scene());
  const cameraRef = useRef<THREE.Camera>(new THREE.Camera());
  const modelRef = useRef<THREE.Object3D>();

  const currentRobotRef = useRef<{
    id: string;
    direction: number;
    ringMaterialRef?: THREE.MeshBasicMaterial;
    status: string;
  }[]>([]);


  let requestId: number;

  const getRobotColor = (status: string) => {
    return {
      backgroundColor: getRobotStatusBackgroundColor(status),
      border: getRobotStatusMainColor(status)
    };
  }

  const loadRobotModel = ({
    direction,
    status,
    robotId
  }: {
    direction: number,
    status: string,
    robotId: string
  }) => {
    const markerDiv = document.createElement("div");

    if (!mapRef) return {
      markerDiv,
      requestId: 0,
      rotate: (_: number) => { }
    };

    const RING_COLOR = getRobotColor(status).border; // Use idle color for ring
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: RING_COLOR,
      side: THREE.DoubleSide,
    });

    currentRobotRef.current = [
      ...currentRobotRef.current.filter(r => r.id !== robotId),
      { id: robotId, direction, ringMaterialRef: ringMaterial, status }
    ];

    // Create a container for the marker
    markerDiv.style.width = "150px";
    markerDiv.style.height = "150px";
    markerDiv.style.background = "transparent";

    // Set up Three.js renderer
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.autoClear = false; // Prevent clearing the canvas
    renderer.setSize(150, 150);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;
    markerDiv.appendChild(renderer.domElement);

    // Setup scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 5);

    const light = new THREE.HemisphereLight(0xffffff, 0x444444, 3.5);
    scene.add(light);

    cameraRef.current = camera;

    let animate: () => void = () => { };
    // Load 3D model
    const loader = new GLTFLoader();
    const dLoader = new DRACOLoader();

    dLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    dLoader.setDecoderConfig({ type: 'js' });

    loader.setDRACOLoader(dLoader);
    loader.load(DEFAULT_MODEL_FILE, (gltf) => {
      const model = gltf.scene;


      model.scale.set(DEFAULT_MODEL_SCALE, DEFAULT_MODEL_SCALE, DEFAULT_MODEL_SCALE); // adjust scale

      // Create ring
      const RING_OUTER_RADIUS = 1.025;
      const RING_INNER_RADIUS = 0.9;
      const ARROW_LENGTH = 1.35;
      const ARROW_BASE_Y = 0.9;
      const ARROW_BASE_WIDTH = 0.5;

      const ringGeometry = new THREE.RingGeometry(RING_INNER_RADIUS, RING_OUTER_RADIUS, 64);
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);

      // Create a bigger triangle arrow
      const arrowShape = new THREE.Shape();
      arrowShape.moveTo(0, ARROW_LENGTH); // tip of the arrow, just outside the ring
      arrowShape.lineTo(-ARROW_BASE_WIDTH / 2, ARROW_BASE_Y); // left base
      arrowShape.lineTo(ARROW_BASE_WIDTH / 2, ARROW_BASE_Y); // right base
      arrowShape.lineTo(0, ARROW_LENGTH); // close the triangle

      const arrowGeometry = new THREE.ShapeGeometry(arrowShape);
      const arrow = new THREE.Mesh(arrowGeometry, ringMaterial);

      // Slightly lift the arrow above the ring to avoid z-fighting
      arrow.position.z = 0.01;

      ring.add(arrow);
      ring.rotation.x = Math.PI / 2
      model.add(ring)

      scene.add(model);

      sceneRef.current = scene
      modelRef.current = model;

      animate = () => {
        requestId = requestAnimationFrame(animate);
        if (model) {
          const map = mapRef;

          const currentDirection = currentRobotRef.current.find(r => r.id === robotId)?.direction || direction || 0;
          const rotationDegree = DEFAULT_MODEL_ROTATION_ADJUSTMENT + map.getBearing() + (currentDirection || 0);

          const ringMaterial = currentRobotRef.current.find(r => r.id === robotId)?.ringMaterialRef;
          const currentStatus = currentRobotRef.current.find(r => r.id === robotId)?.status || status;

          if (ringMaterial) {
            ringMaterial.color.set(getRobotColor(currentStatus).border);
          }

          const bearingRad = THREE.MathUtils.degToRad(rotationDegree);
          const pitchRad = THREE.MathUtils.degToRad(map.getPitch() * (1 - 0.0025));

          // Create a quaternion that combines pitch and bearing
          const qPitch = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(1, 0, 0),
            -pitchRad
          );
          const qBearing = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(0, 1, 0),
            bearingRad
          );

          // Base upright rotation (e.g., -90 degrees to stand upright)
          const base = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(1, 0, 0),
            Math.PI / 2
          );

          // Combine base -> pitch -> bearing
          const finalQuat = new THREE.Quaternion();

          finalQuat
            .multiply(base)
            .multiply(qPitch)
            .multiply(qBearing);

          // Smooth transition using slerp
          scene.quaternion.slerp(finalQuat, 1); // 0.1 = smoothing factor
        }
        renderer.render(scene, camera);
      };
      animate();
    });

    const rotate = (degree: number) => {
      if (!modelRef.current) return;

      const currentRobot = currentRobotRef.current.find(r => r.id === robotId);
      if (!currentRobot) return;

      currentRobotRef.current = [
        ...currentRobotRef.current.filter(r => r.id !== robotId),
        { ...currentRobot, id: robotId, direction: degree }
      ];
    }

    const updateStatus = (status: string) => {
      const ringMaterial = currentRobotRef.current.find(r => r.id === robotId)?.ringMaterialRef
      const currentRobot = currentRobotRef.current.find(r => r.id === robotId);
      if (!currentRobot || !ringMaterial) return;

      ringMaterial.color.set(getRobotColor(status).border)

      currentRobotRef.current = [
        ...currentRobotRef.current.filter(r => r.id !== robotId),
        { ...currentRobot, ringMaterialRef: ringMaterial, status }
      ];
    }

    return {
      markerDiv,
      requestId,
      rotate,
      updateStatus
    };
  }

  const stopAnimation = (animationId?: number) => {
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = undefined;
    }

    cancelAnimationFrame(requestId);
    if (!animationId) {
      return
    }
    try { cancelAnimationFrame(animationId) } catch (error) { }
  }

  return {
    loadRobotModel,
    stopAnimation
  }
}

export const DEFAULT_ZOOM_LEVEL = 18;
